(() => {
  const root = document.documentElement;
  const mobileQuery = window.matchMedia('(max-width: 600px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const registered = new Set();
  let checkFrame = null;
  let checkTimer = null;
  let loadQueued = false;
  let listenersAdded = false;

  const mark = (element, type = 'fade-up', delay = 0) => {
    if (!element || registered.has(element)) return;

    element.dataset.spMotion = type;
    element.style.setProperty('--sp-motion-delay', `${Math.min(delay, 280)}ms`);
    registered.add(element);
  };

  const markAll = (selector, type = 'fade-up', stagger = 0) => {
    document.querySelectorAll(selector).forEach((element, index) => {
      mark(element, type, index * stagger);
    });
  };

  const markGroups = (containerSelector, itemSelector, type = 'fade-up', stagger = 60) => {
    document.querySelectorAll(containerSelector).forEach((container) => {
      container.querySelectorAll(itemSelector).forEach((element, index) => {
        mark(element, type, index * stagger);
      });
    });
  };

  const registerHomepage = () => {
    const heroSequence = [
      ['.sp-hero2__icon', 'zoom-in'],
      ['.sp-hero2__heading', 'fade-up'],
      ['.sp-hero2__text, .sp-hero2__sub', 'fade-up'],
      ['.sp-hero2__btns', 'fade-up'],
      ['.sp-arc', 'zoom-in'],
      ['.sp-trust', 'fade-up']
    ];

    heroSequence.forEach(([selector, type], index) => {
      document.querySelectorAll(selector).forEach((element) => mark(element, type, index * 55));
    });

    markAll('.sp-prods__head, .sp-biz__content, .sp-cats__head', 'fade-up');
    markGroups('.sp-prods__page', '.sp-prod', 'fade-up', 55);
    markGroups('.sp-biz__cards', '.sp-bizcard', 'zoom-in', 90);
    markGroups('.sp-cats__grid', '.sp-card', 'fade-up', 50);
    markGroups('.sp-usps__grid', '.sp-usp', 'fade-up', 85);
    markAll('.sp-visit__media', 'zoom-in');
    markAll('.sp-visit__content', 'fade-up', 80);
  };

  const registerSortiment = () => {
    markAll('.sp-sort-page__head', 'fade-up');
    markGroups('.sp-sort-page__grid', '.sp-sort-card', 'fade-up', 50);
    markAll('.sp-sort-page__quote, .sp-sort-page__quote-image', 'zoom-in');
    markGroups('.sp-sort-usps__grid', '.sp-sort-usp', 'fade-up', 85);
    markAll('.sp-sort-visit__gallery', 'zoom-in');
    markAll('.sp-sort-visit__content', 'fade-up', 80);
  };

  const registerKeimlinge = () => {
    markAll('.sp-kg-hero__background', 'zoom-in');
    const heroContent = [
      ['.sp-kg-hero__content .sp-badge', 'fade-down'],
      ['.sp-kg-hero__heading', 'fade-up'],
      ['.sp-kg-hero__text', 'fade-up']
    ];

    heroContent.forEach(([selector, type], index) => {
      document.querySelectorAll(selector).forEach((element) => mark(element, type, index * 65));
    });

    markGroups('.sp-kg-hero__chips', '.sp-kg-hero__chip', 'fade-up', 80);
    markAll('.sp-kg-usps__head, .sp-kg-sorten__head', 'fade-up');
    markGroups('.sp-kg-usps__grid', '.sp-kg-usp', 'fade-up', 60);
    markAll('.sp-kg-production__media', 'fly-left');
    markAll('.sp-kg-production__content', 'fly-right');

    document.querySelectorAll('.sp-kg-sorten__row').forEach((row) => {
      mark(row.querySelector('.sp-kg-sorten__media'), 'zoom-in');
      mark(row.querySelector('.sp-kg-sorten__content'), 'fade-up', 75);
    });

    markAll('.sp-kg-visit__gallery', 'zoom-in');
    markAll('.sp-kg-visit__content', 'fade-up', 80);
  };

  const registerWir = () => {
    markAll('.sp-wir__intro-image', 'zoom-in');
    markAll('.sp-wir__intro-content', 'fade-up');
    markAll('.sp-wir__story-content', 'fly-left');
    markAll('.sp-wir__story-image', 'fly-right');
    markAll('.sp-wir__team-heading', 'fade-up');
    markAll('.sp-wir__portrait', 'zoom-in', 75);
    markAll('.sp-wir__bio', 'fade-up', 75);
    markAll('.sp-wir-visit__gallery', 'zoom-in');
    markAll('.sp-wir-visit__content', 'fade-up', 80);
  };

  const registerCommerce = () => {
    markAll('.sp-shop-page__head, .sp-contact-page__head', 'fade-up');
    markGroups('.sp-shop-page__grid', '.sp-prod', 'fade-up', 50);
    markAll('.sp-contact-page__form-panel', 'fly-left');
    markAll('.sp-contact-page__location', 'fly-right');
    markAll('.sp-product-page__breadcrumbs', 'fade-down');
    markAll('.sp-product-page__gallery', 'zoom-in');
    markAll('.sp-product-page__info', 'fade-up', 80);
    markGroups('.sp-product-page__benefits', '.sp-product-page__benefit', 'fade-up', 60);
  };

  const registerElements = () => {
    registerHomepage();
    registerSortiment();
    registerKeimlinge();
    registerWir();
    registerCommerce();
  };

  const reveal = (element) => {
    element.classList.add('sp-motion-visible');
  };

  const checkVisible = () => {
    if (checkFrame !== null) cancelAnimationFrame(checkFrame);
    if (checkTimer !== null) clearTimeout(checkTimer);
    checkFrame = null;
    checkTimer = null;
    if (!mobileQuery.matches || reducedMotionQuery.matches) return;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const revealLine = viewportHeight * 0.92;

    registered.forEach((element) => {
      if (!element.isConnected || element.classList.contains('sp-motion-visible')) return;

      const rect = element.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < revealLine) {
        reveal(element);
      }
    });
  };

  const queueCheck = () => {
    if (checkFrame === null) checkFrame = requestAnimationFrame(checkVisible);
    if (checkTimer === null) checkTimer = setTimeout(checkVisible, 80);
  };

  const addViewportListeners = () => {
    if (listenersAdded) return;
    listenersAdded = true;
    window.addEventListener('scroll', queueCheck, { passive: true });
    document.addEventListener('scroll', queueCheck, { passive: true, capture: true });
    window.addEventListener('resize', queueCheck, { passive: true });
  };

  const queueObserver = () => {
    let startQueued = false;
    const start = () => {
      if (startQueued) return;
      startQueued = true;

      let started = false;
      const begin = () => {
        if (started) return;
        started = true;
        root.classList.remove('sp-motion-preparing');
        setTimeout(checkVisible, 28);
      };

      requestAnimationFrame(() => requestAnimationFrame(begin));
      setTimeout(begin, 120);
    };

    if (document.readyState === 'complete') {
      start();
      return;
    }

    if (loadQueued) return;
    loadQueued = true;
      window.addEventListener(
        'load',
      () => {
        loadQueued = false;
        start();
      },
        { once: true }
      );
      setTimeout(start, 260);
    };

  const enable = () => {
    if (!mobileQuery.matches || reducedMotionQuery.matches) {
      root.classList.remove('sp-motion-enabled');
      return;
    }

    registerElements();
    if (!root.classList.contains('sp-motion-enabled')) {
      root.classList.add('sp-motion-preparing');
    }
    root.classList.add('sp-motion-enabled');
    addViewportListeners();
    queueObserver();
  };

  const disable = () => {
    root.classList.remove('sp-motion-enabled', 'sp-motion-preparing');
    if (checkFrame !== null) cancelAnimationFrame(checkFrame);
    if (checkTimer !== null) clearTimeout(checkTimer);
    checkFrame = null;
    checkTimer = null;
  };

  const sync = () => {
    if (mobileQuery.matches && !reducedMotionQuery.matches) enable();
    else disable();
  };

  sync();
  mobileQuery.addEventListener?.('change', sync);
  reducedMotionQuery.addEventListener?.('change', sync);
  document.addEventListener('shopify:section:load', enable);
})();
